import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, XIcon } from "lucide-react";
import { useRef, type ChangeEvent } from "react";
import { cn } from "@/lib/utils";

interface SearchInputProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  className?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  size?: "md" | "lg";
  ariaLabel?: string;
  name?: string;
}

const sizeStyles = {
  md: {
    icon: "left-3 h-4 w-4",
    input: "pl-10 pr-10",
    clear: "right-1.5 size-6",
  },
  lg: {
    icon: "left-4 h-5 w-5",
    input: "pl-12 pr-12 h-12 text-base rounded-xl",
    clear: "right-2.5 size-7",
  },
};

export function SearchInput({
  onSearch,
  placeholder = "Search…",
  className,
  value,
  onChange,
  autoFocus,
  size = "md",
  ariaLabel,
  name = "search",
}: SearchInputProps) {
  const styles = sizeStyles[size];
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const valueSetter = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      "value",
    )?.set;
    valueSetter?.call(input, "");
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();
  };

  return (
    <div className={cn("relative", className)}>
      <Search
        aria-hidden
        className={cn(
          "text-muted-foreground absolute top-1/2 -translate-y-1/2",
          styles.icon,
        )}
      />
      <Input
        aria-label={ariaLabel ?? placeholder}
        name={name}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSearch?.(e.currentTarget.value);
          }
        }}
        className={cn(styles.input)}
        autoFocus={autoFocus}
        ref={inputRef}
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear search"
          className={cn(
            "text-muted-foreground hover:text-foreground absolute top-1/2 -translate-y-1/2 rounded-md",
            styles.clear,
          )}
          onClick={handleClear}
        >
          <XIcon />
        </Button>
      ) : null}
    </div>
  );
}
